import {
    Badge,
    Button,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    LoadingRows,
} from '@shared/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useCan } from '../../auth/hooks/use-can';
import { useReleaseComponent } from '../hooks/use-release-components';
import { useOperationInvalidation, useReleaseOperation } from '../hooks/use-release-operation';

import { ArchiveReleaseDialog } from './ArchiveReleaseDialog';
import { EditReleaseDialog } from './EditReleaseDialog';
import { availableOperationActions } from './operation-confirm';
import { operationStatusView } from './operation-status';
import { releaseMemberRows } from './release-members';
import { releaseNotFound } from './release-page';
import { TurnOnClientsDialog } from './TurnOnClientsDialog';
import { TurnOnReleaseDialog } from './TurnOnReleaseDialog';

/**
 * A release component's own page, at `/catalog/components/release/:id`.
 *
 * Name, version, and a table of its flags with type, default and an override
 * count worded as a lower bound. Edit and archive are offered to engineers only.
 * The status of the last action (from the release row, never from current flag
 * values) and the turn-on control for engineers; turn-off waits for slice 6. Rendered inside the Components section (the router nests it
 * there), so the tab stays marked.
 *
 * An unknown id and an archived one are the same answer — the API returns 404
 * `RELEASE_NOT_FOUND` for both — and both render the not-found state rather
 * than an error panel with nothing to retry.
 */
export function ReleaseComponentPage({ id }: { id: string }) {
    const release = useReleaseComponent(id);
    // Must match `@RequiresRole('engineer')` on PATCH and PUT …/archive.
    const canManage = useCan('engineer');
    // Must match `@RequiresRole('ops-csm')` on POST …/operations/clients.
    const canManageClients = useCan('ops-csm');
    const [dialog, setDialog] = useState<'edit' | 'archive' | 'turn-on' | 'turn-on-clients' | null>(
        null,
    );
    const lastAction = release.data?.lastAction;
    const operation = useReleaseOperation(id, lastAction?.operationId);
    useOperationInvalidation(id, lastAction?.operationId, operation.data?.state, lastAction);
    const navigate = useNavigate();

    const back = (
        <Link
            to="/catalog/components"
            className="text-[13px] font-medium text-accent-text hover:underline"
        >
            All components
        </Link>
    );

    if (release.isPending) {
        return (
            <Card>
                <LoadingRows label="Loading release component…" />
            </Card>
        );
    }

    if (release.isError) {
        return (
            <Card>
                {releaseNotFound(release.error) ? (
                    <EmptyState
                        title="Release component not found"
                        description={`No release component has the id "${id}", or it has been archived.`}
                        action={back}
                    />
                ) : (
                    <ErrorState error={release.error} onRetry={() => void release.refetch()} />
                )}
            </Card>
        );
    }

    const { name, description, releaseVersion, flagKeys } = release.data;
    const rows = releaseMemberRows(release.data.members);
    const status = operationStatusView({
        lastAction,
        operation: operation.data,
        operationMissing: operation.isError && releaseNotFound(operation.error),
    });
    // Must match `@RequiresRole('engineer')` on POST …/operations/all. Turn-off is
    // not reachable here until slice 6 (typed phrase, ACR-1598).
    const operations = availableOperationActions({
        canManage,
        canManageClients,
        hasFlags: flagKeys.length > 0,
        locked: lastAction?.state === 'running' || release.data.activeOperationId !== undefined,
    });

    const actions = (
        <div className="flex items-center gap-3">
            {operations.turnOnClients && (
                <Button
                    size="sm"
                    onClick={() => {
                        setDialog('turn-on-clients');
                    }}
                >
                    Turn on for specific clients
                </Button>
            )}
            {canManage && (
                <>
                    {operations.turnOn && (
                        <Button
                            size="sm"
                            onClick={() => {
                                setDialog('turn-on');
                            }}
                        >
                            Turn on for all clients
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setDialog('edit');
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                            setDialog('archive');
                        }}
                    >
                        Archive
                    </Button>
                </>
            )}
            {back}
        </div>
    );

    return (
        <Card>
            <CardHeader
                title={name}
                subtitle={<Badge tone="brand">Release</Badge>}
                action={actions}
            />
            <div className="flex flex-col gap-5 px-5 py-4">
                <p className="max-w-xl text-[13.5px] leading-relaxed text-content-body">
                    {description}
                </p>
                <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-[13.5px]">
                    <dt className="text-content-muted">Id</dt>
                    <dd>
                        <FlagKeyPill>{id}</FlagKeyPill>
                    </dd>
                    <dt className="text-content-muted">Version</dt>
                    <dd className="font-mono text-content-strong">{releaseVersion}</dd>
                </dl>
                {status.label !== null && (
                    <section aria-label="Status" className="flex flex-col gap-1">
                        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                            Status
                        </h4>
                        <p className="text-[13.5px] font-medium text-content-strong">
                            {status.label}
                        </p>
                        {status.showResults && operation.data && (
                            <p className="text-xs text-content-muted">
                                {operation.data.counters.applied} applied,{' '}
                                {operation.data.counters.skipped} skipped,{' '}
                                {operation.data.counters.failed} failed,{' '}
                                {operation.data.counters.pending} pending
                            </p>
                        )}
                        {status.note !== undefined && (
                            <p className="text-xs text-content-muted">{status.note}</p>
                        )}
                    </section>
                )}
                <section aria-label="Flags">
                    <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                        Flags ({flagKeys.length})
                    </h4>
                    {flagKeys.length === 0 ? (
                        <span className="text-xs text-content-faint">No flags yet</span>
                    ) : (
                        <table className="w-full text-left text-[13px]">
                            <thead>
                                <tr className="text-[11px] uppercase tracking-wide text-content-muted">
                                    <th className="py-1.5 pr-4 font-semibold">Flag</th>
                                    <th className="py-1.5 pr-4 font-semibold">Type</th>
                                    <th className="py-1.5 pr-4 font-semibold">Default</th>
                                    <th className="py-1.5 font-semibold">Client overrides</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.key} className="border-t border-line-subtle">
                                        <td className="py-2 pr-4">
                                            <FlagKeyPill>{row.key}</FlagKeyPill>
                                            {row.archived && (
                                                <span className="ml-2">
                                                    <Badge tone="neutral">Archived</Badge>
                                                </span>
                                            )}
                                            <div className="mt-0.5 text-xs text-content-muted">
                                                {row.name}
                                            </div>
                                        </td>
                                        <td className="py-2 pr-4">{row.typeLabel}</td>
                                        <td className="py-2 pr-4">
                                            <Badge tone={row.defaultOn ? 'success' : 'neutral'}>
                                                {row.defaultLabel}
                                            </Badge>
                                        </td>
                                        <td className="py-2">{row.overrides}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            </div>
            {dialog === 'edit' && (
                <EditReleaseDialog
                    release={release.data}
                    onClose={() => {
                        setDialog(null);
                    }}
                />
            )}
            {dialog === 'turn-on' && (
                <TurnOnReleaseDialog
                    release={release.data}
                    onClose={() => {
                        setDialog(null);
                    }}
                />
            )}
            {dialog === 'turn-on-clients' && (
                <TurnOnClientsDialog
                    release={release.data}
                    onClose={() => {
                        setDialog(null);
                    }}
                />
            )}
            {dialog === 'archive' && (
                <ArchiveReleaseDialog
                    release={release.data}
                    onClose={() => {
                        setDialog(null);
                    }}
                    onArchived={() => {
                        void navigate('/catalog/components');
                    }}
                />
            )}
        </Card>
    );
}
